import type { LocalizedTexts } from "@/types/layout";
import type {
  EntitlementPaymentInfoViewModel,
  EntitlementViewModel,
} from "@/types/entitlements";
import type { SubscriptionPlanTier } from "@/types/plans";
import { t } from "@/utils/translations";
import { getLocale } from "@/utils/locale-store";
import { formatMinorUnitsAsCurrency } from "@/utils/priceFormat";

/**
 * RevenueCat identifiers that denote account-wide GPS Max coverage. Mirrors the
 * backend `RevenueCatEntitlementIdentifiers` / `MemberProductsViewModelBuilder.IsGpsMaxEntitlement`:
 * either the fixed "gps_max" identifier, or a per-slot "device_{N}" identifier whose
 * numeric suffix is a RevenueCat slot index (not a reference to a device in our system).
 */
const GPS_MAX_IDENTIFIER = "gps_max";
const GPS_MAX_DEVICE_PREFIX = "device_";

/**
 * True for a per-slot GPS Max "device_{N}" entitlement (the numeric suffix is a
 * RevenueCat slot index, not a device in our system).
 *
 * GPS Max is reported by RevenueCat as *two* active entitlements at once: the
 * canonical `gps_max` plus one of these per-slot variants. The slot variant has
 * no matching plan tier, so it must not be surfaced as the member's "current
 * plan" — see {@link selectPrimaryEntitlement}.
 */
export function isGpsMaxDeviceSlotEntitlement(
  identifier: string | null | undefined
): boolean {
  if (!identifier) return false;
  if (!identifier.startsWith(GPS_MAX_DEVICE_PREFIX)) return false;
  const suffix = identifier.slice(GPS_MAX_DEVICE_PREFIX.length);
  return suffix.length > 0 && /^\d+$/.test(suffix);
}

/**
 * True when an entitlement identifier denotes GPS Max coverage. Keep in sync with the
 * backend `MemberProductsViewModelBuilder.IsGpsMaxEntitlement`.
 */
export function isGpsMaxEntitlement(
  identifier: string | null | undefined
): boolean {
  return (
    identifier === GPS_MAX_IDENTIFIER ||
    isGpsMaxDeviceSlotEntitlement(identifier)
  );
}

/**
 * Picks the single entitlement to treat as the member's current plan for display
 * and the Change Plan flow.
 *
 * GPS Max arrives as multiple entitlements (the canonical `gps_max` plus a
 * per-slot `device_{N}` variant). The slot variant has no matching plan tier, so
 * if it were chosen the Change Plan picker couldn't resolve the current tier and
 * the user would be unable to change plans. We block the slot variant and prefer
 * any non-slot entitlement, falling back to the first entitlement when *every*
 * entitlement is a slot variant so the UI is never left blank.
 */
export function selectPrimaryEntitlement(
  entitlements: EntitlementViewModel[] | null | undefined
): EntitlementViewModel | null {
  if (!entitlements || entitlements.length === 0) return null;
  const primary = entitlements.find(
    (entitlement) => !isGpsMaxDeviceSlotEntitlement(entitlement.identifier)
  );
  return primary ?? entitlements[0] ?? null;
}

/**
 * True when the member holds at least one active GPS Max entitlement. GPS Max is
 * account-wide (there is no per-device entitlement mapping), so this covers every
 * device the member owns — used to block redundant per-device SIM subscriptions.
 */
export function hasActiveGpsMaxEntitlement(
  entitlements: EntitlementViewModel[] | null | undefined
): boolean {
  return (
    entitlements?.some(
      (entitlement) =>
        entitlement.isActive && isGpsMaxEntitlement(entitlement.identifier)
    ) ?? false
  );
}

/**
 * `devices_{N}` (plural — distinct from the per-slot `device_{N}` variants) is the
 * bundled RevenueCat entitlement carrying how many devices a GPS Max subscription
 * covers: GPS Max is granted as the `gps_max` + `devices_{N}` pair.
 */
const GPS_MAX_DEVICES_COUNT_PREFIX = "devices_";

/**
 * Parses the covered-device count out of a `devices_{N}` entitlement identifier.
 * Returns null for anything else (including the per-slot `device_{N}` variants,
 * whose suffix is a RevenueCat slot index, not a count).
 */
export function parseGpsMaxDeviceCount(
  identifier: string | null | undefined
): number | null {
  if (!identifier) return null;
  if (!identifier.startsWith(GPS_MAX_DEVICES_COUNT_PREFIX)) return null;
  const suffix = identifier.slice(GPS_MAX_DEVICES_COUNT_PREFIX.length);
  if (suffix.length === 0 || !/^\d+$/.test(suffix)) return null;
  const count = Number.parseInt(suffix, 10);
  return count >= 1 ? count : null;
}

/**
 * How many devices the member's active GPS Max subscription covers.
 *
 * GPS Max is normally granted as the `gps_max` + `devices_{N}` entitlement pair,
 * so the count comes from the `devices_{N}` companion. Some members hold only the
 * bare `gps_max` entitlement (the companion grant is missing) — they are treated
 * as covering 1 device so the Change Plan flow can still resolve their current
 * device option instead of dead-ending. Returns null when the member has no
 * active GPS Max coverage at all.
 */
export function getGpsMaxDeviceCount(
  entitlements: EntitlementViewModel[] | null | undefined
): number | null {
  if (!entitlements) return null;
  let count: number | null = null;
  let hasBareGpsMax = false;
  for (const entitlement of entitlements) {
    if (!entitlement.isActive) continue;
    const parsed = parseGpsMaxDeviceCount(entitlement.identifier);
    if (parsed !== null) {
      count = count === null ? parsed : Math.max(count, parsed);
    } else if (isGpsMaxEntitlement(entitlement.identifier)) {
      hasBareGpsMax = true;
    }
  }
  if (count !== null) return count;
  return hasBareGpsMax ? 1 : null;
}

/**
 * True when this entitlement is a non-renewing PROMOTIONAL grant — support-granted access or a
 * redeemed offline licence — rather than something a store is billing.
 *
 * The backend sets `isNonRenewing` to exactly that: a RevenueCat `promotional` entitlement with no
 * live Stripe subscription standing behind it. Named here because two very different rules key off
 * it and "isNonRenewing" reads like a property of a subscription rather than the absence of one:
 * the Subscriptions view disables Cancel, and the Change Plan picker must NOT treat the granted
 * tier as an unbuyable "current plan" — a grant is a temporary gift, not a subscription, and the
 * member has to be able to buy the real thing. Blocking that is what left members who were granted
 * a few days by support unable to purchase the tier they had been granted at all.
 */
export function isNonRenewingGrant(
  entitlement: EntitlementViewModel | null | undefined
): boolean {
  return entitlement?.isNonRenewing === true;
}

/**
 * Resolves the human-friendly plan name for an entitlement.
 *
 * The RevenueCat `identifier` (e.g. "premium") is an internal key, not something
 * we want to show users. The editor-managed display name lives on the matching
 * `subscriptionPlanTier` content (`tierName`), keyed by the same identifier —
 * the same source the Change Plan picker renders. Resolution order:
 *   1. The matching tier's `tierName` (case-insensitive identifier match).
 *   2. A per-identifier dictionary entry (legacy mechanism), if configured.
 *   3. The raw identifier, as a last resort so the UI is never blank.
 *
 * `tiers` may be null/empty while plan tiers are still loading — callers re-render
 * once they arrive, at which point the tierName takes over.
 */
export function resolveEntitlementPlanName(
  identifier: string,
  tiers: SubscriptionPlanTier[] | null | undefined,
  texts: LocalizedTexts
): string {
  const match = tiers?.find(
    (tier) => tier.identifier.toLowerCase() === identifier.toLowerCase()
  );
  if (match?.tierName) {
    return match.tierName;
  }
  return t(
    texts,
    `Tracker.MyAccount.Subscriptions.Plan.${identifier}.Name`,
    identifier
  );
}

/**
 * Returns a copy line explaining where a subscription must be managed when it
 * was purchased through a third-party app store. Returns null for stores we
 * manage in-app (e.g. Stripe) or for unknown / null stores.
 */
export function getExternalManagementMessage(
  store: string | null,
  texts: LocalizedTexts
): string | null {
  if (!store) return null;
  switch (store.toLowerCase()) {
    case "app_store":
    case "mac_app_store":
      return t(
        texts,
        "Tracker.MyAccount.Subscriptions.ManagedByAppStore",
        "To manage this subscription, open the Apple App Store on your device."
      );
    case "play_store":
      return t(
        texts,
        "Tracker.MyAccount.Subscriptions.ManagedByPlayStore",
        "To manage this subscription, open Google Play on your device."
      );
    case "amazon":
      return t(
        texts,
        "Tracker.MyAccount.Subscriptions.ManagedByAmazon",
        "To manage this subscription, open the Amazon Appstore on your device."
      );
    default:
      return null;
  }
}

/**
 * True when the entitlement is managed by a third-party store (App Store /
 * Play Store / Amazon) AND is still set to auto-renew there. Only this state
 * blocks the Change Plan / purchase flow: buying a web plan on top of it would
 * create a double subscription. Once the user cancels in the external store
 * (RevenueCat sets `unsubscribeDetectedAt`) the sub just runs out, so switching
 * to a web-managed plan is allowed again.
 */
export function isRenewingExternallyManaged(
  entitlement: EntitlementViewModel,
  texts: LocalizedTexts
): boolean {
  return (
    getExternalManagementMessage(entitlement.store, texts) !== null &&
    entitlement.unsubscribeDetectedAt === null
  );
}

/**
 * True when the user has cancelled auto-renewal, so the subscription only runs
 * out: either a Stripe sub scheduled to end at the period end, or an external
 * (App Store / Play Store / Amazon) sub the user unsubscribed from in its store.
 * The UI must then present the period-end date as "Expires on" rather than
 * "Next payment on" — no further payment will happen.
 *
 * When `paymentInfo` is present it is the ONLY signal consulted: it is a live read
 * of the Stripe subscription the member will actually be billed for, whereas
 * RevenueCat's `unsubscribeDetectedAt` is keyed by product and can describe a
 * different, outgoing subscription on the same product — which is how members with
 * a cancelling sub and a successor taking over ended up being told their membership
 * was ending. `unsubscribeDetectedAt` remains the signal for external stores, where
 * there is no Stripe subscription and so no `paymentInfo`.
 */
export function isRenewalCancelled(entitlement: EntitlementViewModel): boolean {
  if (entitlement.paymentInfo) {
    return entitlement.paymentInfo.cancelAtPeriodEnd === true;
  }
  return entitlement.unsubscribeDetectedAt !== null;
}

/** The dates every Stripe-backed subscription the backend sends carries. */
export interface BillingDates {
  currentPeriodEnd: string | null;
  /** Optional because older cached responses may not carry it. */
  nextPaymentDate?: string | null;
  /** Optional because older cached responses may not carry it. */
  endsAt?: string | null;
}

/**
 * The date to show beside "Next payment on" — or, for a plan set to end, beside "Expires on" /
 * "Ends" — for any Stripe-backed subscription: the member's own plan and the ones they pay for.
 *
 * - Renewing: the backend's `nextPaymentDate`. A subscription extended with a free trial phase is
 *   paid up to its period end but not charged again until the trial ends, and showing the period end
 *   told members they would be charged a whole extension early.
 * - Set to end: `nextPaymentDate` is null and `endsAt` is when access ends. For a cancelled extended
 *   subscription that is the end of the free extension, which a cancel keeps.
 * - Older responses without either: the period end, as before.
 */
export function resolveBillingDate(billing: BillingDates): string | null {
  return billing.nextPaymentDate ?? billing.endsAt ?? billing.currentPeriodEnd;
}

/**
 * `resolveBillingDate` for the member's own plan, falling back to RevenueCat's `expiresAt` for
 * externally-managed plans, which carry no `paymentInfo`.
 */
export function resolveNextPaymentDate(
  entitlement: EntitlementViewModel
): string | null {
  const payment = entitlement.paymentInfo;
  return (
    (payment ? resolveBillingDate(payment) : null) ??
    entitlement.expiresAt ??
    null
  );
}

/**
 * Whether a discount code can be redeemed against this subscription (GROW-328). Requires a Stripe-managed
 * subscription we have payment info for (external/promotional grants have no `paymentInfo`) that is not
 * already scheduled to cancel at period end — a discount only affects future renewals, and a cancelling
 * subscription has none. Shared by the SubscriptionsView action row and the RedeemDiscountView gate.
 */
export function canRedeemSubscriptionDiscount(
  entitlement: EntitlementViewModel | null | undefined
): boolean {
  const payment = entitlement?.paymentInfo;
  if (!payment) return false;
  return payment.cancelAtPeriodEnd === false;
}

/**
 * Formats an ISO date in the SITE's locale (e.g. "13 januari 2027" on sv-SE),
 * not the browser's — a Swedish page rendered "13 January 2027" for anyone whose
 * browser was set to English. Returns the input verbatim when it can't be
 * parsed, and an em dash for a missing value.
 */
export function formatDate(dateString: string | null): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString(getLocale().language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * formatDate's compact sibling (e.g. "11 mar. 2026" on sv-SE), for places where the long
 * form would be wider than the column it sits in — a date range, or a value next to a price.
 *
 * The option set is the short date the rest of My Account already uses (OrderDetailView,
 * OrdersListView, B2bDashboard, paymentFormat). Those all pass `undefined` as the locale,
 * i.e. the BROWSER's, which is the very bug the note on formatDate describes; this one takes
 * the site's, so prefer it over adding another local copy.
 */
export function formatShortDate(dateString: string | null): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString(getLocale().language, {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

/**
 * Formats a minor-unit amount in the given currency. Returns null when the amount or currency is
 * missing. Shared implementation behind {@link formatPrice} and {@link formatMinorAmount}.
 *
 * Delegates to the shared formatter so these amounts render in the SITE's locale like every other
 * price on the page — a browser default rendered "SEK 486.40" beside the API's own "1 199,00 kr".
 */
function formatCurrencyFromMinor(
  amountMinor: number | null | undefined,
  currency: string | null | undefined
): string | null {
  if (amountMinor == null || !currency) return null;
  return formatMinorUnitsAsCurrency(amountMinor, currency);
}

/**
 * Renders the Stripe price (minor units + currency) in the user's locale.
 * Returns null when we don't have enough info to format anything sensible.
 */
export function formatPrice(
  payment: EntitlementPaymentInfoViewModel
): string | null {
  return formatCurrencyFromMinor(payment.priceAmount, payment.currency);
}

/**
 * Formats an arbitrary minor-unit amount in the given currency (used for the struck-through full
 * price next to a discounted price). Returns null when inputs are missing.
 */
export function formatMinorAmount(
  amountMinor: number | null | undefined,
  currency: string | null | undefined
): string | null {
  return formatCurrencyFromMinor(amountMinor, currency);
}

/**
 * Returns a translated billing interval suffix, e.g. " / per year", " / per month".
 * Handles intervalCount > 1 (e.g. "every 3 months") with a generic fallback.
 */
/**
 * Takes only the interval fields, not the whole payment view model, so managed subscriptions
 * (which carry the same two fields under a different shape) reuse the interval labels instead of
 * duplicating them.
 */
export function formatBillingInterval(
  payment: Pick<EntitlementPaymentInfoViewModel, "interval" | "intervalCount">,
  texts: LocalizedTexts
): string {
  const count = payment.intervalCount ?? 1;
  if (count === 1) {
    switch (payment.interval) {
      case "year":
        return t(
          texts,
          "Tracker.MyAccount.Subscriptions.PerYear",
          " / per year"
        );
      case "month":
        return t(
          texts,
          "Tracker.MyAccount.Subscriptions.PerMonth",
          " / per month"
        );
      case "week":
        return t(
          texts,
          "Tracker.MyAccount.Subscriptions.PerWeek",
          " / per week"
        );
      case "day":
        return t(texts, "Tracker.MyAccount.Subscriptions.PerDay", " / per day");
      default:
        return "";
    }
  }
  return ` / ${count} ${payment.interval ?? ""}`.trimEnd();
}

export function formatCardBrand(brand: string | null): string {
  if (!brand) return "";
  // Stripe gives lowercase brands like "visa", "mastercard", "amex".
  return brand
    .split(/[\s_-]/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/** Narrow parameter for the same reason as {@link formatBillingInterval}. */
export function formatCardLine(
  payment: Pick<EntitlementPaymentInfoViewModel, "cardBrand" | "cardLast4">
): string | null {
  if (!payment.cardLast4) return null;
  const brand = formatCardBrand(payment.cardBrand);
  const mask = `•••• ${payment.cardLast4}`;
  return brand ? `${brand} ${mask}` : mask;
}
